/**
 * Utility to intelligently extract field schema from API responses
 * Handles arrays, nested objects, and creates flat field names with dot notation
 */

const ENVELOPE_KEYS = new Set([
  'outputdata',
  'output_data',
  'data',
  'result',
  'results',
  'payload',
  'body',
  'response',
  'value',
  'items',
  'records',
  'content',
  'rows',
  'locations',
  'vehicles',
  'drivers',
  'entities',
])

const isPlainObject = (value: unknown): value is Record<string, any> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const MESSAGE_ENVELOPE_KEYS = new Set(['message_id', 'schema_version', 'entity', 'occurred_at'])

/**
 * Peel API envelopes ({ outputData: [...] }, { data, pagination }) and
 * use the first array record as the mapping schema.
 * Do not peel a destination message envelope ({ message_id, payload }).
 */
export function unwrapPayloadEnvelope(data: any, depth = 0): any {
  if (data == null || depth > 6) return data

  if (Array.isArray(data)) {
    const first = data.find((item: any) => item != null)
    if (first && typeof first === 'object') return unwrapPayloadEnvelope(first, depth + 1)
    return data
  }

  if (!isPlainObject(data)) return data

  const keys = Object.keys(data)
  if (keys.length === 0) return data

  const looksLikeMessageEnvelope =
    keys.some(key => MESSAGE_ENVELOPE_KEYS.has(key.toLowerCase())) &&
    keys.some(key => key.toLowerCase() === 'payload')
  if (looksLikeMessageEnvelope) return data

  if (keys.length === 1) {
    const key = keys[0]
    const value = data[key]
    if (Array.isArray(value)) {
      if (value.length === 0) return data
      if (value.some((item: any) => item && typeof item === 'object')) {
        return unwrapPayloadEnvelope(value, depth + 1)
      }
    } else if (isPlainObject(value) && ENVELOPE_KEYS.has(key.toLowerCase())) {
      return unwrapPayloadEnvelope(value, depth + 1)
    }
  }

  const nested = keys
    .map(key => ({ key, value: data[key], envelope: ENVELOPE_KEYS.has(key.toLowerCase()) }))
    .filter(entry => entry.value && typeof entry.value === 'object')

  const named = nested.filter(entry => entry.envelope)
  const pick =
    named.find(entry => Array.isArray(entry.value) && entry.value.some((item: any) => item && typeof item === 'object')) ||
    nested.find(entry => Array.isArray(entry.value) && entry.value.some((item: any) => item && typeof item === 'object'))

  if (pick && (named.length > 0 || keys.length <= 4)) {
    return unwrapPayloadEnvelope(pick.value, depth + 1)
  }

  return data
}

const LIST_KEYS = ['drivers', 'vehicles', 'geofences', 'results', 'value', 'data', 'items', 'records', 'list']

/** OData `{ value: [...] }`, mock `{ data: [...] }`, and similar list envelopes. */
export function extractRecordList(sourceData: any): any[] | null {
  if (Array.isArray(sourceData)) {
    return sourceData.some(item => item && typeof item === 'object') ? sourceData : null
  }
  if (!isPlainObject(sourceData)) return null
  for (const key of LIST_KEYS) {
    const list = sourceData[key]
    if (Array.isArray(list) && list.some((item: any) => item && typeof item === 'object')) {
      return list
    }
  }
  return null
}

export function firstRecord(sourceData: any): any {
  const records = extractRecordList(sourceData)
  if (records && records.length > 0) return records[0]
  const unwrapped = unwrapPayloadEnvelope(sourceData)
  return isHollowPayload(unwrapped) ? sourceData : unwrapped
}

/** True when a payload has no record fields to map (e.g. `{ outputData: [] }`). */
export function isHollowPayload(data: any): boolean {
  if (data == null) return true
  if (Array.isArray(data)) return data.length === 0 || data.every((item: any) => isHollowPayload(item))
  if (typeof data !== 'object') return false
  const keys = Object.keys(data)
  if (keys.length === 0) return true
  return keys.every(key => {
    const value = data[key]
    if (Array.isArray(value)) return value.length === 0
    if (value && typeof value === 'object') return isHollowPayload(value)
    return false
  })
}

export function schemaFromMappingEdges(edges: Array<{ sourcePath?: string; sourceHandle?: string }> | undefined): Record<string, string> | undefined {
  const schema: Record<string, string> = {}
  ;(Array.isArray(edges) ? edges : []).forEach(edge => {
    const raw = String(edge.sourcePath || edge.sourceHandle || '').replace(/-source$/i, '')
    if (!raw || raw === 'source') return
    schema[raw.replace(/\./g, '_')] = ''
  })
  return Object.keys(schema).length > 0 ? schema : undefined
}

export interface ExtractedField {
  fieldName: string; // Flat name with dot notation (e.g., "address.city")
  value: any; // Sample value from the response
  type: string; // Inferred data type
  path: string; // Path to access the value
  isNested: boolean; // Whether this field is nested
  parent?: string; // Parent field name if nested
}

/**
 * Extract fields from API response payload
 * @param payload - The API response (object or array)
 * @param options - Configuration options
 * @returns Object with flat field names as keys and sample values
 */
export function extractPayloadFields(
  payload: any,
  options: {
    maxDepth?: number; // Maximum nesting depth to traverse
    arrayBehavior?: 'first' | 'all'; // How to handle arrays
    includeArrayIndex?: boolean; // Include array indices in field names
  } = {}
): Record<string, any> {
  const {
    maxDepth = 10,
    arrayBehavior = 'first',
    includeArrayIndex = false
  } = options;

  const extractedFields: Record<string, any> = {};
  const root = unwrapPayloadEnvelope(payload);

  /**
   * Recursively extract fields from an object
   */
  function extractFromObject(
    obj: any,
    prefix: string = '',
    depth: number = 0
  ): void {
    // Stop if max depth reached
    if (depth >= maxDepth) {
      if (prefix) {
        extractedFields[prefix] = obj;
      }
      return;
    }

    // Handle null/undefined
    if (obj === null || obj === undefined) {
      if (prefix) {
        extractedFields[prefix] = obj;
      }
      return;
    }

    // Handle primitive types
    if (typeof obj !== 'object') {
      if (prefix) {
        extractedFields[prefix] = obj;
      }
      return;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      if (obj.length === 0) {
        return;
      }

      if (arrayBehavior === 'first') {
        // Process only the first item to get schema
        const firstItem = obj[0];
        
        // If array contains objects, extract their fields
        if (typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem)) {
          extractFromObject(firstItem, prefix, depth);
        } else {
          // Array of primitives
          if (prefix) {
            extractedFields[prefix] = firstItem;
          }
        }
      } else {
        // Process all items (may result in duplicates, but keeps all variations)
        obj.forEach((item, index) => {
          // ✅ CRITICAL: Sanitize array notation - replace brackets with underscores
          const itemPrefix = includeArrayIndex 
            ? (prefix ? `${prefix}_${index}` : `${index}`)
            : prefix;
          
          if (typeof item === 'object' && item !== null) {
            extractFromObject(item, itemPrefix, depth);
          } else {
            if (itemPrefix) {
              extractedFields[itemPrefix] = item;
            }
          }
        });
      }
      return;
    }

    // Handle objects
    for (const key in obj) {
      if (!obj.hasOwnProperty(key)) continue;

      const value = obj[key];
      // ✅ CRITICAL: Sanitize key to replace dots with underscores for React Flow handle compatibility
      // This ensures field names like "csv_attachments.data.camera" become "csv_attachments_data_camera"
      const sanitizedKey = key.replace(/\./g, '_').replace(/\[/g, '_').replace(/\]/g, '_');
      const fieldName = prefix ? `${prefix}_${sanitizedKey}` : sanitizedKey;

      // Recursively process nested objects/arrays
      if (typeof value === 'object' && value !== null) {
        extractFromObject(value, fieldName, depth + 1);
      } else {
        // Primitive value - add to extracted fields
        extractedFields[fieldName] = value;
      }
    }
  }

  // Start extraction
  extractFromObject(root);

  return extractedFields;
}

/**
 * Get detailed field information with types
 */
export function getDetailedFieldInfo(payload: any): ExtractedField[] {
  const flatFields = extractPayloadFields(payload);
  const detailedFields: ExtractedField[] = [];

  for (const [fieldName, value] of Object.entries(flatFields)) {
    const pathParts = fieldName.split('.');
    const isNested = pathParts.length > 1;
    const parent = isNested ? pathParts.slice(0, -1).join('.') : undefined;

    detailedFields.push({
      fieldName,
      value,
      type: inferType(value),
      path: fieldName,
      isNested,
      parent
    });
  }

  return detailedFields;
}

/**
 * Infer data type from value
 */
function inferType(value: any): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return 'array';
  
  const type = typeof value;
  
  // Check for date strings
  if (type === 'string') {
    const dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;
    if (dateRegex.test(value)) {
      return 'date';
    }
  }
  
  // Check for number strings
  if (type === 'string' && !isNaN(Number(value)) && value.trim() !== '') {
    return 'number-string';
  }
  
  return type;
}

/**
 * Convert flat fields back to nested object structure
 * (Useful for reverse transformation)
 */
export function unflattenFields(flatFields: Record<string, any>): any {
  const result: any = {};

  for (const [path, value] of Object.entries(flatFields)) {
    // Split by underscore since we now use underscores instead of dots
    const parts = path.split('_');
    let current = result;

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!current[part]) {
        current[part] = {};
      }
      current = current[part];
    }

    current[parts[parts.length - 1]] = value;
  }

  return result;
}

/**
 * Example usage:
 * 
 * const apiResponse = {
 *   user: {
 *     name: "John",
 *     address: {
 *       city: "NYC",
 *       zip: "10001"
 *     }
 *   }
 * };
 * 
 * const fields = extractPayloadFields(apiResponse);
 * // Result: {
 * //   "user.name": "John",
 * //   "user.address.city": "NYC",
 * //   "user.address.zip": "10001"
 * // }
 */



